
import { useState } from 'react';
import { Acquirer, ConnectedAccount } from './types';
import UnavailableAcquirerNotice from './UnavailableAcquirerNotice';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import { formatRut } from '@/lib/rut';
import { useActivation } from '@/contexts/ActivationContext';

interface AddConnectionFormProps {
  acquirers: Acquirer[];
  existingIds: string[];
  onConnect: (account: ConnectedAccount) => void;
  onCancel?: () => void;
  showCancel?: boolean;
}

export default function AddConnectionForm({
  acquirers,
  existingIds,
  onConnect,
  onCancel,
  showCancel = false,
}: AddConnectionFormProps) {
  const { refreshStatus } = useActivation();
  const availableAcquirers = acquirers.filter((a) => !existingIds.includes(a.id));
  const [selectedId, setSelectedId] = useState(availableAcquirers[0]?.id ?? '');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const selected = availableAcquirers.find((a) => a.id === selectedId) ?? availableAcquirers[0];

  const handleAcquirerChange = (id: string) => {
    setSelectedId(id);
    setFieldValues({});
  };

  const handleFieldChange = (name: string, value: string) => {
    setFieldValues((prev) => ({
      ...prev,
      [name]: name === 'rut' ? formatRut(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected?.available) return;
    setSaveError(null);
    setSubmitting(true);
    try {
      const url = getEndpointUrl('acquirerConnections', 'save');
      const rawResponse = await httpClient.post(url, {
        slug: selected.id,
        ...fieldValues,
      });
      let backendId: string | undefined;
      try {
        const responseData = await rawResponse.json();
        backendId = responseData?.id;
      } catch {
        backendId = undefined;
      }
      await refreshStatus();
      onConnect({
        id: backendId ?? `${selected.id}-${Date.now()}`,
        acquirerId: selected.id,
        acquirerSlug: selected.id,
        acquirerName: selected.name,
        icon: selected.icon,
        iconBg: selected.iconBg,
        iconColor: selected.iconColor,
        rut: fieldValues['rut'] ?? '',
        status: 'syncing',
      });
      setFieldValues({});
      setSelectedId(availableAcquirers[0]?.id ?? '');
    } catch {
      setSaveError('No se pudo guardar la conexión. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (availableAcquirers.length === 0) {
    return (
      <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
        <span className="material-symbols-outlined text-base">warning</span>
        Ya tienes un adquirente conectado. Solo se permite uno por empresa.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Adquirente
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="material-symbols-outlined text-slate-500 text-lg">credit_card</span>
          </div>
          <select
            value={selectedId}
            onChange={(e) => handleAcquirerChange(e.target.value)}
            className="block w-full pl-10 pr-10 py-3 text-base border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-primary focus:border-primary text-sm rounded-lg shadow-sm appearance-none"
          >
            {availableAcquirers.map((a) => (
              <option key={a.id} value={a.id}>
                {a.selectLabel}
                {!a.available ? ' — Próximamente' : ''}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className="material-symbols-outlined text-slate-400 text-lg">expand_more</span>
          </div>
        </div>
      </div>

      {selected?.available ? (
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              {selected.fields.map((field) => (
                <div key={field.name}>
                  <label
                    htmlFor={`field-${field.name}`}
                    className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >
                    {field.label}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-slate-400 text-lg">
                        {field.icon}
                      </span>
                    </div>
                    <input
                      id={`field-${field.name}`}
                      name={field.name}
                      type={field.type}
                      value={fieldValues[field.name] ?? ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                      className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                  </div>
                  {field.hint && (
                    <p className="mt-1.5 text-xs text-slate-500">{field.hint}</p>
                  )}
                </div>
              ))}
            </div>

            {saveError && (
              <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                <span className="material-symbols-outlined text-base flex-shrink-0">error</span>
                {saveError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 mt-6">
              {showCancel && onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
              >
                {submitting ? (
                  <>
                    <span className="material-symbols-outlined text-base animate-spin">
                      progress_activity
                    </span>
                    Conectando...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-base">link</span>
                    Conectar Adquirente
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <UnavailableAcquirerNotice
          acquirerName={selected?.name ?? ''}
          onCancel={onCancel ?? (() => {})}
        />
      )}
    </div>
  );
}
